import beta from '@/assets/icons/beta.svg';
import { ReactComponent as BookingsIcon } from '@/assets/icons/bookings-black.svg';
import { ReactComponent as ProductIcon } from '@/assets/icons/product-icon.svg';
import { Button, Dialog, HSpacer, Logo, Text, VSpacer } from '@/components/DesignSystem';
import {
  DefaultRetailerSelector,
  useDefaultRetailerId,
  withRetailerIdParam,
} from '@/components/shared/DefaultRetailerSelector';
import { UserSwitcher } from '@/components/shared/UserSwitcher';
import { AppConfig } from '@/constants/AppConfig';
import { QueryKeys } from "@/constants/QueryKeys";
import { Routes } from '@/constants/Routes';
import { SessionState, useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { OrderTransactionsApi } from "@/utilities/api/OrderTransactionsApi";
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import BugReport from '@mui/icons-material/BugReport';
import Dashboard from '@mui/icons-material/Dashboard';
import Factory from '@mui/icons-material/Factory';
import Flag from '@mui/icons-material/Flag';
import HelpOutline from '@mui/icons-material/HelpOutline';
import Insights from '@mui/icons-material/Insights';
import LocalOffer from '@mui/icons-material/LocalOffer';
import People from '@mui/icons-material/People';
import ReceiptLong from '@mui/icons-material/ReceiptLong';
import Storefront from '@mui/icons-material/Storefront';
import Email from '@mui/icons-material/Email';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MenuIcon from '@mui/icons-material/Menu';
import Phone from '@mui/icons-material/Phone';
import {
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  useTheme,
} from '@mui/material';

import {
  GrowersContactInfo,
  hasUserPermission,
  UserPermission,
  UserType,
} from '@shared/enums';
import { formatPhoneNumber, getFarmerName, isInternalUser, ISO8601String } from '@shared/utilities';
import { DateTime } from 'luxon';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from "react-query";
import { useLocation, useNavigate } from 'react-router-dom';

export const AppHeader = () => {
  const { t, i18n } = useTranslation();
  const {
    isInternal, isRetailerAdmin, logout, platformLinks, retailer, sessionState, user,
  } = useAuthentication();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const { defaultRetailerId, setDefaultRetailerId } = useDefaultRetailerId();
  const theme = useTheme();
  const retailerImageUrl = retailer?.image && `${AppConfig.staticImageHost}/${retailer.image}`;

  const canUserAccessRetailerOverview = (
    user?.userType === UserType.RetailerAdmin
    || (user?.userType === UserType.SalesPerson && user.defaultSalesperson)
  );

  const { data: latestProcessedOrderDate } = useQuery(
    [QueryKeys.GET_LATEST_PROCESSED_ORDER_DATE, retailer?.id],
    () => OrderTransactionsApi.getLatestProcessedOrderDate({
      retailerId: retailer?.id,
    }),
    { enabled: !!retailer?.id },
  );

  const formatDate = (date: Date | ISO8601String) => {
    const dateObj = (typeof date === 'string')
      ? DateTime.fromISO(date.toString()).toJSDate()
      : date;
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  };

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate(Routes.LOGIN, { replace: true });
  };

  const handleNavigation = (menuItem: MenuItem) => {
    navigate(getMenuItemPath(menuItem));
    setOpen(false);
  };

  const isSelected = (route: Routes) => {
    return location.pathname.includes(route);
  };

  interface MenuItem {
    icon?: ReactNode,
    label: string | ReactNode,
    route: Routes,
    hidden?: boolean,
    defaultRetailerPath?: (retailerId: string) => string,
  }

  const getMenuItemPath = (menuItem: MenuItem) => (
    isInternal && defaultRetailerId && menuItem.defaultRetailerPath
      ? menuItem.defaultRetailerPath(defaultRetailerId)
      : menuItem.route
  );

  const canAccessNotificationBuilder = (
    isInternal
    || (
      isRetailerAdmin
      && hasUserPermission(user?.permissions, UserPermission.CanAccessNotificationsBuilder)
    )
  );

  const AdminMenuItems: MenuItem[] = [
    {
      icon: <People />,
      label: t($ => $.customers.title),
      route: Routes.CUSTOMERS,
    },
    {
      icon: <Storefront />,
      label: t($ => $.app.navigation.retailers),
      route: Routes.ADMIN_HIERARCHY_OF_RETAILERS,
      defaultRetailerPath: (retailerId) => `${Routes.ADMIN_HIERARCHY_OF_RETAILERS}/${retailerId}`,
    },
    {
      icon: <Factory />,
      label: t($ => $.app.navigation.manufacturers),
      route: Routes.ADMIN_LIST_MANUFACTURERS,
    },
    {
      icon: <ProductIcon />,
      label: t($ => $.app.navigation.products),
      route: Routes.PRODUCTS,
    },
    {
      icon: <LocalOffer />,
      label: t($ => $.app.navigation.promotions),
      route: Routes.PROMOTIONS,
    },
    {
      icon: <FormatListBulletedIcon />,
      label: t($ => $.app.navigation.reports),
      route: Routes.REPORTS,
      defaultRetailerPath: (retailerId) => withRetailerIdParam(Routes.REPORTS, retailerId),
    },
    {
      icon: <ReceiptLong />,
      label: t($ => $.app.navigation.orders),
      route: Routes.ADMIN_LIST_ORDERS,
    },
    {
      icon: <BookingsIcon height={24} width={24} />,
      label: t($ => $.bookings.title),
      route: Routes.ADMIN_BOOKINGS,
    },
    {
      icon: <Email />,
      label: t($ => $.app.navigation.notificationBuilder),
      route: Routes.ADMIN_NOTIFICATION_BUILDER,
    },
    {
      icon: <AdminPanelSettings />,
      label: t($ => $.app.navigation.adminUsers),
      route: Routes.ADMIN_LIST_ADMINS,
    },
    {
      icon: <Flag />,
      label: t($ => $.app.navigation.featureFlags),
      route: Routes.FEATURE_FLAGS,
    },
    {
      icon: <BugReport />,
      label: t($ => $.app.navigation.debug),
      route: Routes.ADMIN_DEBUG,
      defaultRetailerPath: (retailerId) => (
        withRetailerIdParam(Routes.ADMIN_POINTS_AUDIT, retailerId)
      ),
    },
  ];

  const RetailerMenuItems: MenuItem[] = [];

  if (canUserAccessRetailerOverview) {
    RetailerMenuItems.push({
      icon: <Dashboard />,
      label: t($ => $.app.navigation.overview),
      route: Routes.RETAILER_OVERVIEW,
    });
    if (retailer?.isChurnEnabled) {
      RetailerMenuItems.push({
        icon: <Insights />,
        label: (
          <Stack alignItems="center" direction="row" spacing={1}>
            {t($ => $.app.navigation.insights)}
            <HSpacer size="1" />
            <img alt={t($ => $.app.labels.beta)} height={16} src={beta} />
          </Stack>
        ),
        route: Routes.RETAILER_INSIGHTS,
      });
    }
  }

  const canAccessReports = (
    hasUserPermission(user?.permissions, UserPermission.CanAccessReports)
    || (user?.userType === UserType.SalesPerson && user.defaultSalesperson)
  );

  if (canAccessReports) {
    RetailerMenuItems.push({
      icon: <FormatListBulletedIcon/>,
      label: t($ => $.app.navigation.reports),
      route: Routes.REPORTS,
    });
  }

  RetailerMenuItems.push(...[
    {
      icon: <People />,
      label: t($ => $.customers.title),
      route: Routes.CUSTOMERS,
    },
    {
      icon: <LocalOffer />,
      label: t($ => $.app.navigation.promotions),
      route: Routes.PROMOTIONS,
    },
    ...(retailer?.hasBookings ? [{
      icon: <BookingsIcon height={24} width={24} />,
      label: t($ => $.bookings.title),
      route: Routes.ADMIN_BOOKINGS,
    }] : []),
    {
      icon: <ProductIcon />,
      label: t($ => $.app.navigation.products),
      route: Routes.PRODUCTS,
    },
    {
      icon: <Email />,
      label: t($ => $.app.navigation.notificationBuilder),
      route: Routes.NOTIFICATION_BUILDER,
      hidden: !canAccessNotificationBuilder,
    },
  ]);

  const formatVersion = (version: string) =>
    version.match(/\d+\.\d+\.\d+/) ? 'v' + version : version;
  const version = (AppConfig.env.test || isInternalUser(user)) && (
    <Text>{formatVersion(AppConfig.version)}</Text>
  );

  const menuItems = (isInternal ? AdminMenuItems : RetailerMenuItems).filter(item => !item.hidden);
  return (
    <Stack
      alignItems="center"
      bgcolor={theme.palette.background.default}
      borderBottom="1px solid rgba(0, 0, 0, 0.04)"
      direction="row"
      height="64px"
      justifyContent="space-between"
      p="8px 32px 8px 24px"
      position="fixed"
      width="100%"
      zIndex={999}
    >
      <Stack alignItems="center" direction="row" spacing={2}>
        {sessionState === SessionState.LOGGED_IN && user && (
          <>
            <IconButton edge="start" onClick={handleClick}>
              <MenuIcon />
            </IconButton>
            {platformLinks.map(({ code, url }) => (
              <Button
                key={code}
                onClick={() => window.location.assign(url)}
                startIcon={<SwapHoriz />}
                testID={`switch-to-${code}`}
                variant="text"
              >
                {t($ => $.app.navigation.switchToProduct, { product: code })}
              </Button>
            ))}
            {!!retailerImageUrl && (
              <img
                alt={t($ => $.app.navigation.retailerLogo, { name: retailer.name })}
                height={32}
                onClick={() => navigate(Routes.HOMEPAGE)}
                src={retailerImageUrl}
              />
            )}
            {!retailer && (
              <Logo
                onClick={() => navigate(Routes.HOMEPAGE)}
                size="tiny"
              />
            )}
          </>
        )}
        <UserSwitcher />
        {sessionState === SessionState.LOGGED_IN && isInternal && (
          <DefaultRetailerSelector onChange={setDefaultRetailerId} value={defaultRetailerId} />
        )}
      </Stack>
      <Stack alignItems="center" direction="row" spacing={2}>
        {!!latestProcessedOrderDate && (
          <Text category="body-small">
            {t($ => $.app.navigation.lastUpdated, { date: formatDate(latestProcessedOrderDate) })}
          </Text>
        )}
        {isInternal && (
          <Stack alignItems='center' direction='row' spacing={2}>
            {version}
            <Text>{getFarmerName(user, true)}</Text>
          </Stack>
        )}
        <Button
          onClick={() => setShowHelpDialog(true)}
          startIcon={<HelpOutline />}
          testID="help-button" variant="text"
        >
          {t($ => $.common.actions.help)}
        </Button>
      </Stack>
      <Drawer
        anchor="left"
        elevation={5}
        onClose={handleClose}
        open={open}
        sx={{ borderRadius: '0px 24px 24px 0px', padding: '32px 0px 24px 0px' }}
      >
        <Stack flexGrow={1} paddingBottom="24px" paddingTop="32px" width="316px">
          <List sx={{ py: 0, width: '100%' }}>
            {menuItems.map((menuItem) => {
              const selected = isSelected(menuItem.route);

              return (
                <ListItem key={menuItem.route}>
                  <ListItemButton
                    onClick={() => handleNavigation(menuItem)}
                    selected={selected}
                    sx={{
                      '&.Mui-selected .MuiListItemText-root span': {
                        color: theme.palette.primary.contrastText,
                      },
                      '&.Mui-selected:hover .MuiListItemText-root span': {
                        color: theme.palette.primary.contrastText,
                      },
                      '&.Mui-selected .MuiListItemIcon-root': {
                        color: theme.palette.primary.contrastText,
                      },
                      '&.Mui-selected:hover .MuiListItemIcon-root': {
                        color: theme.palette.primary.contrastText,
                      },
                    }}
                  >
                    {!!menuItem.icon && (
                      <ListItemIcon>
                        {menuItem.icon}
                      </ListItemIcon>
                    )}
                    <ListItemText primary={menuItem.label} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          <Stack alignItems="center" flexGrow={1} justifyContent="flex-end">
            <Button
              onClick={handleLogout}
              testID="logout-button"
              variant="text"
            >
              {t($ => $.common.actions.logout)}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
      {showHelpDialog && (
        <Dialog
          onClose={() => setShowHelpDialog(false)}
          open
          testID="help-dialog"
          title={t($ => $.app.support.contactTitle, { name: 'Growers' })}
        >
          <Divider />
          <VSpacer size="5" />
          <Stack direction="row" justifyContent="space-between">
            <Stack>
              <Text category="overline">{t($ => $.common.contact.phoneNumber)}</Text>
              <Text category="body-large">
                {formatPhoneNumber(GrowersContactInfo.supportPhone, 'paren')}
              </Text>
            </Stack>
            <Button
              onClick={() => document.location.href = `tel:${GrowersContactInfo.supportPhone}`}
              startIcon={<Phone />}
              testID="call-support-button"
            >
              {t($ => $.common.actions.call)}
            </Button>
          </Stack>
          <VSpacer size="5" />
          <Divider />
          <VSpacer size="5" />
          <Stack direction="row" justifyContent="space-between">
            <Stack>
              <Text category="overline">{t($ => $.common.contact.email)}</Text>
              <Text category="body-large">
                {GrowersContactInfo.supportEmail}
              </Text>
            </Stack>
            <Button
              onClick={() => document.location.href = `mailto:${GrowersContactInfo.supportEmail}`}
              startIcon={<Email />}
              testID="email-support-button"
            >
              {t($ => $.common.actions.email)}
            </Button>
          </Stack>
          <VSpacer size="5" />
          <Divider />
        </Dialog>
      )}
    </Stack>
  );
};
